import styled from '@emotion/styled';
import { InlineTagNodeWrap } from '../card/CommonCardStyles';
import { IconName } from '../images/IconMap';
import { LabelLarge, LightXSmall } from '../text/Typography';
import {
    ButtonRow,
    LinkRow,
    RowChildrenContainer,
    RowContent,
    RowEndIcon,
    RowStartIcon,
    RowWrapper,
} from './CommonRowStyles';

interface IActionRowProps {
    title: string;
    description?: string;
    disabled?: boolean;
    startIcon?: IconName;
    endIcon?: IconName;
    iconSize?: number;
    href?: string;
    onClick?: (
        event:
            | React.MouseEvent<HTMLButtonElement>
            | React.KeyboardEvent<HTMLButtonElement>
            | React.TouchEvent<HTMLButtonElement>
    ) => void;
    children?: React.ReactNode;
    tagNode?: React.ReactNode;
}

export default function ActionRow({
    title,
    description,
    disabled = false,
    startIcon,
    endIcon = 'forward',
    href,
    iconSize = 42,
    onClick,
    children,
    tagNode,
    ...restProps
}: IActionRowProps) {
    const commonProps = {
        disabled,
    };
    if (!href && !onClick) throw new Error('ActionRow requires either an href or onClick prop');

    if (href) {
        return (
            <RowWrapper>
                <LinkRow href={href} {...commonProps} {...restProps}>
                    {startIcon && <RowStartIcon name={startIcon} size={iconSize} />}
                    <RowContent>
                        {tagNode ? (
                            <ActionRowTitle as="span">
                                {tagNode} {title}
                            </ActionRowTitle>
                        ) : (
                            <ActionRowTitle as="span">{title}</ActionRowTitle>
                        )}
                        {description && <LightXSmall as="span">{description}</LightXSmall>}
                        {children && <RowChildrenContainer>{children}</RowChildrenContainer>}
                    </RowContent>
                    {endIcon && <RowEndIcon name={endIcon} size={iconSize} />}
                </LinkRow>
            </RowWrapper>
        );
    } else {
        return (
            <RowWrapper>
                <ButtonRow onClick={onClick} {...commonProps} {...restProps}>
                    {startIcon && <RowStartIcon name={startIcon} size={iconSize} />}
                    <RowContent>
                        {tagNode ? (
                            <InlineTagNodeWrap>
                                {tagNode} <ActionRowTitle as="span">{title}</ActionRowTitle>
                            </InlineTagNodeWrap>
                        ) : (
                            <ActionRowTitle as="span">{title}</ActionRowTitle>
                        )}
                        {description && <LightXSmall as="span">{description}</LightXSmall>}
                        {children && <RowChildrenContainer>{children}</RowChildrenContainer>}
                    </RowContent>
                    {endIcon && <RowEndIcon name={endIcon} size={iconSize} />}
                </ButtonRow>
            </RowWrapper>
        );
    }
}

const ActionRowTitle = styled(LabelLarge)`
    color: inherit;
`;
